import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { Input, Card, Select, Row, Col, Form } from "antd";
import { countryState } from "../../../constants/country-state";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";

const { TextArea } = Input;
const { Option } = Select;

const LeadForm = () => {
  // STATES
  const [selectedCountry, setCountry] = useState(null);
  const [types, setTypes] = useState([]);

  // REDUX DATA
  const company = useSelector((state) => state.CompanyReducer.selected_company);
  const { leadsList } = useSelector((state) => state.LeadsListReducer);

  // CONST VALS
  const { id } = useParams();
  const [form] = Form.useForm();
  const { http } = global.services;
  const history = useHistory();
  const states = countryState[selectedCountry];

  // HELPERS
  const getLeadsHelper = async () => {
    if (!id) return;
    let { data } = await http.get(`user-lead/${id}`);
    form.setFieldsValue({
      ...data,
      reasoning: data.recommended_lead_notes?.reasoning,
      list: data.list ?? "default",
    });
    setCountry(data.country);
  };

  const getTypesHelper = async () => {
    if (!company?.id) return;
    let { data } = await http.get("communication", {
      user_company_id: company.id,
    });
    setTypes(data);
  };

  // HANDLERS
  const backHandler = () => {
    history.goBack();
  };

  const submitHandler = (values) => {
    const payload = {
      ...values,
      is_recommended: 0,
      list: values.list === "default" ? null : values.list,
      user_company_id: company.id,
      recommended_lead_notes: {
        reasoning: values.reasoning,
      },
    };
    if (id) payload["id"] = parseInt(id);

    const method = id
      ? () => http.put("user-lead", payload)
      : () => http.post("user-lead", payload);

    method()
      .then(() => {
        toast.success(`Lead ${id ? "updated" : "created"} successfully`);
        history.push("/leads-manager");
      })
      .catch((error) => {
        const errorMessage = error?.response?.data?.error || "Something went wrong";
        toast.error(errorMessage);
      });
  };

  // HOOKS
  useEffect(() => {
    getTypesHelper();
  }, [company]);

  useEffect(() => {
    getLeadsHelper();
  }, [id]);

  return (
    <div className="leads-form-wrapper">
      <h2 className="page-title">
        <button className="back-btn" onClick={backHandler}>
          <ArrowLeftOutlined />
        </button>
        {id ? "Update" : "Create"} Lead
      </h2>
      <Card bordered={false}>
        <Form onFinish={submitHandler} form={form} initialValues={{ list: "default" }}>
          <Row gutter={[24, 0]}>
            <Col xs={24} md={12}>
              <label htmlFor="name">Name</label>
              <Form.Item
                name="lead_name"
                rules={[
                  { required: true, message: "Name is required" },
                  {
                    max: 100,
                    message: "Name should not exceed 100 characters",
                  },
                ]}
              >
                <Input id="name" placeholder="Enter name" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <label htmlFor="url">URL</label>
              <Form.Item
                name="url"
                rules={[
                  { required: true, message: "URL is required" },
                  {
                    pattern: new RegExp(
                      /^(https?:\/\/)?([\w-]+(\.[\w-]+)+)(\/[\w-./?%&=]*)?$/
                    ),
                    message: "Enter a valid URL",
                  },
                  {
                    max: 100,
                    message: "URL should not exceed 100 characters",
                  },
                ]}
              >
                <Input id="url" placeholder="e.g https://www.example.com" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <label htmlFor="contact_number">Contact Number</label>
              <Form.Item
                name="contact_number"
                rules={[
                  {
                    pattern: new RegExp(/^\+?[1-9]\d{1,14}$/),
                    message: "Enter a valid contact number",
                  },
                  { max: 15, message: "Contact number should not exceed 15 digits" },
                ]}
              >
                <Input id="contact_number" placeholder="Enter contact number" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <label htmlFor="email">Email</label>
              <Form.Item
                name="email"
                rules={[
                  {
                    pattern: new RegExp(
                      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
                    ),
                    message: "Enter a valid email address",
                  },
                  { max: 100, message: "Email should not exceed 100 characters" },
                ]}
              >
                <Input id="email" placeholder="Enter email" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <label htmlFor="country">Country</label>
              <Form.Item name="country">
                <Select
                  id="country"
                  placeholder="Select country"
                  defaultValue={selectedCountry}
                  onChange={(val) => setCountry(val)}
                  getPopupContainer={(trigger) => trigger.parentElement}
                >
                  {Object.keys(countryState).map((value) => {
                    return <Option value={value}>{value}</Option>;
                  })}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <label htmlFor="state">State</label>
              <Form.Item name="state">
                <Select
                  id="state"
                  placeholder="Select state"
                  getPopupContainer={(trigger) => trigger.parentElement}
                >
                  {states?.map((value) => {
                    return <Option value={value}>{value}</Option>;
                  })}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <label htmlFor="status">Status</label>
              <Form.Item
                name="status"
                rules={[{ required: true, message: "Status is required" }]}
              >
                <Select
                  id="status"
                  placeholder="Select status"
                  getPopupContainer={(trigger) => trigger.parentElement}
                >
                  {types.map((type, index) => (
                    <Option key={index} value={type.status} className="capitalize">
                      {type.status}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <label htmlFor="list">List</label>
              <Form.Item
                name="list"
                rules={[{ required: true, message: "List is required" }]}
              >
                <Select
                  id="list"
                  placeholder="Select a list"
                  getPopupContainer={(trigger) => trigger.parentElement}
                >
                  <Option value="default">General</Option>
                  {leadsList.map((list, index) => (
                    <Option key={index} value={list.id}>
                      {list.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24}>
              <label htmlFor="user_notes">Reasoning</label>
              <Form.Item name="reasoning">
                <TextArea id="reasoning" rows={3} />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <label htmlFor="user_notes">Custom user notes</label>
              <Form.Item name="user_notes">
                <TextArea id="user_notes" rows={6} />
              </Form.Item>
            </Col>
          </Row>
          <div className="action-wrapper">
            <button>Submit</button>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default LeadForm;
